import { useTranslation } from 'module/hooks/use-translation';
import { ReactComponent as InspectorAI } from 'module/ui/assets/svgs/inspector-ai.svg';
import { faCircleArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@rocketlane/ui';
import { Textarea } from '@rocketlane/ui';
import { FC, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { triggerChat } from 'components/organisms/chat/use-chat-trigger';

const Wrapper = styled.div<{ leftPosition: number }>`
  position: absolute;
  bottom: 32px;
  left: ${(props) => props.leftPosition}px;
  background: #fff;
  box-shadow: var(--shadow-16);
  border-radius: var(--spacing-03);
  padding: var(--spacing-02) var(--spacing-04) var(--spacing-02)
    var(--spacing-02);
  z-index: 1000;
  display: flex;
  align-items: center;
  width: 750px;
  box-shadow: var(--shadow-16);
  max-height: 40px;

  .rl-textarea-wrapper {
    width: 100%;
  }
`;

const InspectorContinue = styled(FontAwesomeIcon)`
  color: var(--icon-secondary);
`;

const StyledTextArea = styled(Textarea)`
  height: 32px !important;
  border: none !important;
  padding: var(--spacing-03) var(--spacing-03) !important;
  outline: none !important;

  &::placeholder,
  textarea::placeholder {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:focus,
  &:hover {
    border: none !important;
    outline: none !important;
  }
`;
const StyledButton = styled.button<{ isInspectorEnabled: boolean }>`
  background: ${({ isInspectorEnabled }) =>
    isInspectorEnabled ? 'var(--scarlet-gray-50)' : 'transparent'};
  height: 32px;
  width: 32px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--spacing-03);

  &:hover {
    background: var(--scarlet-gray-50);
  }
`;

export const Action = styled(Button).attrs({
  variant: 'ghost',
})<{ isInspectorEnabled: boolean }>`
  --action-hover-background: var(--item-hover-bg);
  width: 100%;
  &:hover,
  &:focus,
  &:active {
    background: ${({ isInspectorEnabled }) =>
      isInspectorEnabled ? 'var(--scarlet-gray-50)' : 'transparent'};
  }
`;

export const TextFormattingToolbar: FC<{
  inspectorEnabled: boolean;
  setIsElementInspectorOpen: (isOpen: boolean) => void;
  pageType?: string;
  documentId?: number;
  title?: string;
  documentSources?: {
    sourceId: number;
    sourceType: string;
  }[];
}> = ({
  inspectorEnabled,
  setIsElementInspectorOpen,
  pageType,
  title,
  documentId,
  documentSources,
}) => {
  const placeholderText = useTranslation('ask_ai_about', {
    title,
  });
  const [text, setText] = useState('');
  const [leftPosition, setLeftPosition] = useState(0);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Calculate position relative to rich text editor
  const calculatePosition = () => {
    const rteEditor = document.querySelector('.rte-editor');

    if (!rteEditor || !toolbarRef.current) {
      return;
    }

    const editorRect = rteEditor.getBoundingClientRect();
    const toolbarWidth = 750; // Fixed width from CSS
    const parentRect =
      toolbarRef.current.parentElement?.getBoundingClientRect();

    if (parentRect) {
      // Calculate the left position to center the toolbar relative to the editor
      const editorCenter = editorRect.left + editorRect.width / 2;
      const toolbarLeft = editorCenter - toolbarWidth / 2;
      const relativeLeft = toolbarLeft - 55;

      setLeftPosition(Math.max(0, relativeLeft));
    }
  };

  // Use callback ref to recalculate position when toolbar mounts
  const setToolbarRef = (node: HTMLDivElement | null) => {
    (toolbarRef as React.MutableRefObject<HTMLDivElement | null>).current =
      node;
    if (node) {
      // Small delay to ensure DOM is ready
      setTimeout(calculatePosition, 0);
    }
  };

  // Recalculate position when component mounts
  useEffect(() => {
    calculatePosition();
  }, []);

  const handleSendMessage = () => {
    if (!text.trim()) {
      return;
    } // Don't send empty messages

    // Use the new triggerChat function instead of custom events
    triggerChat({
      message: text.trim(),
      elementContext: '',
      sourceType: pageType || '',
      sourceId: documentId || -1,
      sources: documentSources || undefined,
    });
    // Clear the text area after sending
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      handleSendMessage();
    }
  };

  return (
    <Wrapper ref={setToolbarRef} leftPosition={leftPosition}>
      <StyledButton
        isInspectorEnabled={inspectorEnabled}
        onClick={() => setIsElementInspectorOpen(!inspectorEnabled)}
      >
        <InspectorAI />
      </StyledButton>
      <StyledTextArea
        placeholder={placeholderText}
        className="text-sm"
        onChange={(e) => setText(e.target.value)}
        value={text}
        rows={1}
        maxRows={5}
        onKeyDown={handleKeyDown}
      />
      <Button
        leftIcon={<InspectorContinue icon={faCircleArrowRight} />}
        size="small"
        variant="ghost"
        disabled={!text.trim()}
        onClick={handleSendMessage}
      />
    </Wrapper>
  );
};
